import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { FaYoutube } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 50%, #f0f4ff 100%)' 
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: ${props => props.themeMode === 'light' ? '#333' : '#fff'};
  padding: 2rem;
  overflow-x: hidden;
`;

const Header = styled.div`
  text-align: center;
  padding: 4rem 2rem 2rem 2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  background: linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #b388ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const YoutubeIcon = styled.span`
  color: #ff0000;
  font-size: 3rem;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.themeMode === 'light' ? '#666666' : '#b39ddb'};
  max-width: 600px;
  margin: 0 auto;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 3rem auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 0.5rem;
  }
`;

const VideoCard = styled(motion.div)`
  background: ${props => props.themeMode === 'light' 
    ? '#ffffff' 
    : 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.2)' 
    : 'rgba(179, 157, 219, 0.2)'};
  box-shadow: ${props => props.themeMode === 'light' 
    ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
    : 'none'};
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-10px);
    border-color: #7c4dff;
    box-shadow: 0 20px 40px rgba(124, 77, 255, 0.3);
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)' 
    : 'linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  font-size: 1.2rem;
  border: 2px dashed #7c4dff;
`;

const VideoInfo = styled.div`
  padding: 1.5rem;
`;

const VideoTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.themeMode === 'light' ? '#333333' : '#b39ddb'};
  margin-bottom: 0.5rem;
`;

const VideoDescription = styled.p`
  color: ${props => props.themeMode === 'light' ? '#666666' : '#a0a0c0'};
  font-size: 0.95rem;
  line-height: 1.6;
`;

const SubscribeSection = styled.section`
  text-align: center;
  padding: 4rem 2rem;
  background: ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.02)'};
  border-radius: 20px;
  max-width: 800px;
  margin: 3rem auto;
`;

const SubscribeButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 2.5rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #ff0000 0%, #cc0000 100%);
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 8px 30px rgba(255, 0, 0, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(255, 0, 0, 0.5);
  }
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

// Scroll animation hook
const useScrollAnimation = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  
  return { ref, controls };
};

function Videos() {
  const { theme } = useTheme();
  const { data } = useData();
  const { t } = useLanguage();
  const themeMode = theme.mode;
  const { ref, controls } = useScrollAnimation();

  // Get videos from data context
  const videos = data.videos || [];

  return (
    <Main themeMode={themeMode}>
      <Header>
        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <YoutubeIcon><FaYoutube /></YoutubeIcon>
          {t.videos.title}
        </Title>
        <Subtitle
          themeMode={themeMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t.videos.subtitle}
        </Subtitle>
      </Header>

      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={staggerContainer}
      >
        <VideosGrid>
          {videos.map((video, index) => (
            <VideoCard
              key={video.id}
              themeMode={themeMode}
              variants={fadeInUp}
              whileHover={{ scale: 1.02 }}
            >
              <VideoWrapper>
                {video.youtubeId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.youtubeId}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <VideoPlaceholder themeMode={themeMode}>
                    <FaYoutube style={{ fontSize: '4rem', marginBottom: '1rem', color: '#ff0000' }} />
                    {t.videos.addVideoId}
                  </VideoPlaceholder>
                )}
              </VideoWrapper>
              <VideoInfo>
                <VideoTitle themeMode={themeMode}>{video.title}</VideoTitle>
                <VideoDescription themeMode={themeMode}>{video.description}</VideoDescription>
              </VideoInfo>
            </VideoCard>
          ))}
        </VideosGrid>
      </motion.div>

      <SubscribeSection themeMode={themeMode}>
        <motion.h2
          style={{ 
            fontSize: '2rem', 
            marginBottom: '1rem',
            background: 'linear-gradient(90deg, #7c4dff 0%, #536dfe 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {t.videos.subscribe}
        </motion.h2>
        <motion.p
          style={{ color: themeMode === 'light' ? '#666666' : '#a0a0c0', marginBottom: '2rem', fontSize: '1.1rem' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {t.videos.subscribeDesc}
        </motion.p>
        <SubscribeButton
          href={data.socialLinks?.youtube || "https://youtube.com/@YourChannelHere"}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaYoutube /> {t.videos.subscribeBtn}
        </SubscribeButton>
      </SubscribeSection>
    </Main>
  );
}

export default Videos;
